import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Conversation\ndescription: Conversation enables you to create a new conversation and return the conversation ID.\n---\n\n\n## New Conversation\n\nThe `newConversation` endpoint is part of the Mendable API, designed to create a new conversation and return the conversation ID.\n\n```bash\nPOST /api.mendable.ai/v0/newConversation\n```\n\n### Example Usage\n\n### Request\n\nHere is an example request using cURL:\n\n```bash\ncurl -X POST https://api.mendable.ai/v0/newConversation \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n        \"api_key\": \"YOUR_API_KEY\",\n      }'\n```\n\nor using Javascript:\n\n```js\nconst url = \"https://api.mendable.ai/v0/newConversation\";\n\nconst data = {\n  api_key: \"YOUR_API_KEY\",\n};\n\nfetch(url, {\n  method: \"POST\",\n  headers: {\n    \"Content-Type\": \"application/json\",\n  },\n  body: JSON.stringify(data),\n})\n  .then((response) => response.json())\n  .then((data) => console.log(data))\n  .catch((error) => console.error(\"Error:\", error));\n\n```\n\n### Response\n\nHere is an example response:\n\n```json\n{\n  \"conversation_id\": 1234567890\n}\n```\n\nThe conversation_id is return as an integer. You can use this to start a [chat](/mendable-api/chat).\n\n\n### Request Paremeters\n\n| Field     | Type   | Required | Description                                                                 |\n|-----------|--------|----------|-----------------------------------------------------------------------------|\n| api_key  | string | Yes      | Your Mendable API key     |\n\n\n\n\n\n\n\n## End Conversation (Optional)\n\nThe `endConversation` endpoint is part of the Mendable API, designed to end a conversation. This helps keep track of conversation time, useful for analytics.\n\n```bash\nPOST /api.mendable.ai/v0/endConversation\n```\n\n### Example Usage\n\n#### Request\n\nHere is an example request using cURL:\n\n```bash\ncurl -X POST https://api.mendable.ai/v0/endConversation \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n        \"api_key\": \"YOUR_API_KEY\",\n        \"conversation_id\": 123,\n      }'\n```\n\n```js\n\nconst url = \"https://api.mendable.ai/v0/endConversation\";\n\nconst data = {\n  \"api_key\": \"YOUR_API_KEY\",\n  \"conversation_id\": 123,\n};\n\nfetch(url, {\n  method: \"POST\",\n  headers: {\n    \"Content-Type\": \"application/json\",\n  },\n  body: JSON.stringify(data),\n})\n  .then((response) => response.json())\n  .then((data) => console.log(data))\n  .catch((error) => console.error(\"Error:\", error));\n\n```\n\n#### Response\n\nHere is an example response:\n\n```json\nConversation has ended.\n```\n";
const filepath = "/mendable-api/conversation.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
